import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  OnboardingCallsProps,
  OnboardingCall,
  AgentEvents,
  StructuredCallCreatedEvent,
  StructuredCallUpdatedEvent,
} from '@dreamai/types';
import { usePusher } from '../../../../contexts/PusherContext';
import api from '../../../../api';
import './onboarding-calls.scss';
import { ReactComponent as CaretDown } from '../../../../icons/down-caret.svg';
import { ReactComponent as CaretUp } from '../../../../icons/up-caret.svg';
import { ReactComponent as StartNowIcon } from '../../../../icons/start-now.svg';
import { ReactComponent as CalendarIcon } from '../../../../icons/calendar-schedule.svg';

// Placeholder icon components - TODO: replace with actual icons
const MoreIcon = () => <div className='icon-placeholder'>⋯</div>;

interface OnboardingCallsComponentProps extends OnboardingCallsProps {
  threadId?: string;
}

const OnboardingCalls: React.FC<OnboardingCallsComponentProps> = ({
  threadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true) as { userChannel?: any } | null;
  const userChannel = pusherContext?.userChannel;
  const [calls, setCalls] = useState<OnboardingCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [schedulingCallIds, setSchedulingCallIds] = useState<Set<string>>(
    new Set(),
  );

  const fetchOnboardingCalls = useCallback(async () => {
    if (!threadId) {
      setLoading(false);
      setError('Thread ID is required');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get(
        `/agents/threads/${threadId}/onboarding-calls`,
      );

      if (response.data.success) {
        setCalls(response.data.calls);
        // Auto-expand the first incomplete call
        const firstIncomplete = response.data.calls.find(
          (call: OnboardingCall) => call.status !== 'completed',
        );
        if (firstIncomplete) {
          setExpandedCallId(firstIncomplete.id);
        }
      } else {
        setError(response.data.error || 'Failed to load onboarding calls');
      }
    } catch (err) {
      console.error('Error fetching onboarding calls:', err);
      setError('Failed to load onboarding calls');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, threadId]);

  // Initial fetch
  useEffect(() => {
    fetchOnboardingCalls();
  }, [fetchOnboardingCalls]);

  // Listen for structured call created/updated Pusher events
  useEffect(() => {
    if (!userChannel) return;

    const handleStructuredCallCreated = (data: StructuredCallCreatedEvent) => {
      // Only refetch if it's an onboarding call
      if (data.isOnboarding) {
        console.log('Onboarding call created, refetching...');
        fetchOnboardingCalls();
      }
    };

    const handleStructuredCallUpdated = (data: StructuredCallUpdatedEvent) => {
      // Refetch to get updated status
      console.log('Structured call updated, refetching...');
      fetchOnboardingCalls();
    };

    userChannel.bind(
      AgentEvents.STRUCTURED_CALL_CREATED,
      handleStructuredCallCreated,
    );
    userChannel.bind(
      AgentEvents.STRUCTURED_CALL_UPDATED,
      handleStructuredCallUpdated,
    );

    return () => {
      userChannel.unbind(
        AgentEvents.STRUCTURED_CALL_CREATED,
        handleStructuredCallCreated,
      );
      userChannel.unbind(
        AgentEvents.STRUCTURED_CALL_UPDATED,
        handleStructuredCallUpdated,
      );
    };
  }, [userChannel, fetchOnboardingCalls]);

  const toggleExpand = (callId: string) => {
    setExpandedCallId(expandedCallId === callId ? null : callId);
  };

  const getStatusLabel = (status: OnboardingCall['status']) => {
    switch (status) {
      case 'draft':
        return 'Not started';
      case 'scheduled':
        return 'Scheduled';
      case 'in_progress':
        return 'In progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const handleStartCall = async (call: OnboardingCall) => {
    if (!threadId) {
      console.error('Cannot start call: threadId is required');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await api.post(`/agents/structured-calls/${call.id}/start`, {
        threadId,
      });
    } catch (err) {
      console.error('Error starting call:', err);
    }
  };

  const handleScheduleCall = async (call: OnboardingCall) => {
    if (!threadId) {
      console.error('Cannot schedule call: threadId is required');
      return;
    }

    // Mark as scheduling immediately
    setSchedulingCallIds((prev) => new Set(prev).add(call.id));

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await api.post(`/agents/structured-calls/${call.id}/schedule`, {
        threadId,
      });

      // Re-enable after 60 seconds
      setTimeout(() => {
        setSchedulingCallIds((prev) => {
          const next = new Set(prev);
          next.delete(call.id);
          return next;
        });
      }, 60000);
    } catch (err) {
      console.error('Error triggering schedule flow:', err);
      // Re-enable on error
      setSchedulingCallIds((prev) => {
        const next = new Set(prev);
        next.delete(call.id);
        return next;
      });
    }
  };

  const handleMoreOptions = (call: OnboardingCall) => {
    // TODO: Implement more options menu
    console.log('More options:', call.id);
  };

  if (loading) {
    return (
      <div className='onboarding-calls'>
        <div className='loading'>Loading onboarding calls...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='onboarding-calls'>
        <div className='error'>{error}</div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className='onboarding-calls'>
        <div className='empty'>No onboarding calls scheduled.</div>
      </div>
    );
  }

  return (
    <div className='onboarding-calls'>
      {calls.map((call) => {
        const isExpanded = expandedCallId === call.id;
        const isCompleted = call.status === 'completed';

        return (
          <div
            key={call.id}
            className={`call-item ${isExpanded ? 'expanded' : ''} ${isCompleted ? 'completed' : ''}`}
          >
            <div className='call-header' onClick={() => toggleExpand(call.id)}>
              <div className='call-info'>
                <h3 className='call-title'>{call.title || call.purpose}</h3>
                {isExpanded && call.purpose && (
                  <p className='call-description'>{call.purpose}</p>
                )}
              </div>
              <div className='call-expand-icon'>
                {isExpanded ? <CaretUp /> : <CaretDown />}
              </div>
            </div>

            {isExpanded && (
              <div className='call-details'>
                <div className='call-actions'>
                  <span className='status-label'>
                    {getStatusLabel(call.status)}
                  </span>
                  {!isCompleted && (
                    <>
                      <button
                        className='action-button primary'
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartCall(call);
                        }}
                      >
                        <StartNowIcon />
                        <span>Start now</span>
                      </button>
                      {call.scheduledAt ? (
                        <button
                          className='action-button primary scheduled'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CalendarIcon />
                          <span>
                            Scheduled:{' '}
                            {new Date(call.scheduledAt).toLocaleString(
                              'en-GB',
                              {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            )}
                          </span>
                        </button>
                      ) : (
                        <button
                          className='action-button primary'
                          disabled={schedulingCallIds.has(call.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScheduleCall(call);
                          }}
                        >
                          <CalendarIcon />
                          <span>
                            {schedulingCallIds.has(call.id)
                              ? 'See chat'
                              : 'Schedule'}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OnboardingCalls;
